import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

const normHeader = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const looksLikeRecoveryLedger = (tabs = []) => tabs.some((tab) => {
  const h = new Set((tab.headers || []).map(normHeader));
  const hasPaidNsf = h.has("paidnsf") || h.has("paymentdraftstatus") || h.has("draftstatus");
  const hasCaseCore = h.has("carrier") && h.has("status") && (h.has("premium") || h.has("monthlypremium"));
  const hasDraftHistory = [...h].some((x) => x.includes("draft") && (x.includes("chargeback") || /^\d(st|nd|rd|th)?draft/.test(x)));
  return hasPaidNsf && hasCaseCore && (hasDraftHistory || h.has("draftdate"));
});

// Guided presentation demo-context flag. When active, every request is tagged so the
// backend scopes reads/writes to the isolated demo agency (validated server-side).
let _presentationActive = false;
export function setPresentationActive(v) { _presentationActive = !!v; }
api.interceptors.request.use(async (config) => {
  // Dashboard reads must remain read-only. Reconciliation/recovery is always an explicit
  // workflow action, never a side effect of opening a reporting page.
  if (config.url === "/dashboard/stats") config.url = "/dashboard/stats-v2";

  // Import commits are not allowed to outrun source lineage. Persist the exact review
  // decisions (including explicit __skip__ mappings) before the operational commit.
  if (config.url === "/import/commit" && config.data?.session_id) {
    await api.post(`/import/archive-source/${config.data.session_id}/review-decisions`, {
      tabs: config.data.tabs || [],
    });
  }

  if (_presentationActive) {
    config.headers = config.headers || {};
    config.headers["X-Presentation-Demo"] = "1";
  }
  return config;
});

api.interceptors.response.use(async (response) => {
  // Generic Import: preserve the legitimate source cells before any operational commit,
  // then redirect recovery-ledger-shaped workbooks to the dedicated recovery workflow.
  if (response.config?.url === "/import/analyze" && response.data?.session_id && response.config?.data instanceof FormData) {
    const originalFile = response.config.data.get("file");
    if (originalFile) {
      const fd = new FormData();
      fd.append("session_id", response.data.session_id);
      fd.append("file", originalFile);
      const archived = await api.post("/import/archive-source", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      response.data.source_lineage = archived.data;
    }
    if (looksLikeRecoveryLedger(response.data.tabs || [])) {
      const err = new Error("Existing Book Recovery workbook detected");
      err.response = {
        status: 409,
        data: { detail: "This workbook contains payment/draft/chargeback recovery tracking. Use Existing Book Recovery instead of generic Import Data so every case is reconciled against carrier status, first premium and commissions." },
      };
      err.recoveryRoute = "/recovery";
      throw err;
    }
  }

  // Existing Book Recovery: the same upload is archived cell-by-cell, with formulas,
  // scalar types, number formats, coordinates and headers encrypted before the user can
  // commit recovery cases. Prohibited security-code columns remain excluded by policy.
  if (response.config?.url === "/book-recovery/analyze" && response.data?.session_id && response.config?.data instanceof FormData) {
    const originalFile = response.config.data.get("file");
    if (originalFile) {
      const fd = new FormData();
      fd.append("session_id", response.data.session_id);
      fd.append("file", originalFile);
      const archived = await api.post("/book-recovery/archive-source", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      response.data.source_lineage = archived.data;
    }
  }
  return response;
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Voice preview returns a Blob for audio, including on JSON HTTP errors.
export async function formatApiError(error, fallback = "Something went wrong. Please try again.") {
  let data = error?.response?.data;
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try { data = JSON.parse(await data.text()); } catch { return fallback; }
  }
  if (data?.detail != null) return formatApiErrorDetail(data.detail);
  return fallback;
}

export default api;
